/**
 * API client. Session is an httpOnly cookie set by /auth/login; every request also sends the tenant header.
 * Errors are normalised to ApiError {code, message, details, status}.
 */
import { IDLE_HEADER, SESSION_CONFLICT } from '@pqrs/contracts';
import { idleSeconds } from '@/app/activity';
import { isLeaving } from '@/features/auth/suiteSso';
/** Quien quiera enterarse de que la sesión ya no sirve (401 fuera del ingreso): la sesión vuelve al ingreso. */
let unauthorized: ((code: string) => void) | null = null;
export function onUnauthorized(fn: (code: string) => void) { unauthorized = fn; }
/**
 * Un 401 manda a ingresar **una** vez por página (GUIA §3.1, «Varias pestañas»): las demás peticiones que fallen después
 * no vuelven a avisar. Mientras la pestaña sale («Salir», continuar en la otra sesión), ninguno.
 */
let unauthorizedSent = false;
function notifyUnauthorized(code: string | undefined) {
  if (!unauthorized || unauthorizedSent || isLeaving()) return;
  unauthorizedSent = true;
  unauthorized(code ?? 'UNAUTHENTICATED');
}
/** Quien quiera enterarse de que la sesión está en espera por otro ingreso (409 `SESSION_CONFLICT`): pregunta en cuál seguir. */
let sessionConflict: (() => void) | null = null;
export function onSessionConflict(fn: () => void) { sessionConflict = fn; }

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly details?: unknown, public readonly correlationId?: string) {
    super(message);
  }
}

/**
 * Entidad que recuerda este navegador: `?tenant=slug` es como llega desde la suite (el portal, el selector de apps y la
 * vuelta del ingreso) y `?entidad=slug` el atajo de desarrollo (`pnpm tenant:create` imprime el enlace); cualquiera de
 * las dos queda guardada, y vacía la olvida.
 */
function rememberedTenant(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const asked = params.get('tenant') ?? params.get('entidad');
    if (asked !== null) {
      if (asked.trim()) localStorage.setItem('pqrs.entidad', asked.trim());
      else localStorage.removeItem('pqrs.entidad');
    }
    return localStorage.getItem('pqrs.entidad') || null;
  } catch {
    return null;
  }
}
const BUILT_TENANT = import.meta.env.VITE_TENANT || null;
/**
 * La que recuerda el navegador, si alguna. El ingreso con la suite pide solo esta: sin ella la suite entra a la única
 * entidad de la persona o le pide elegir, y la vuelta la deja guardada.
 */
export const REMEMBERED_TENANT = rememberedTenant();
/**
 * Entidad a la que habla esta app: la recordada o, si no, la de la compilación (`VITE_TENANT`, para SGDEA sin la suite).
 * `null`: ninguna (sin el encabezado, el API usa la de la sesión o `DEFAULT_TENANT_SLUG` si existe).
 */
export const TENANT = REMEMBERED_TENANT ?? BUILT_TENANT;
/** El encabezado de la entidad, solo si ya se conoce una. */
const tenantHeader = (): Record<string, string> => (TENANT ? { 'x-tenant-id': TENANT } : {});
const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export async function api<T>(path: string, init: RequestInit & { json?: unknown; query?: Record<string, unknown> } = {}): Promise<T> {
  const { json, query, headers, ...rest } = init;
  let url = `${BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
      else qs.set(k, String(v));
    }
    const q = qs.toString();
    if (q) url += `?${q}`;
  }
  const res = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: { ...tenantHeader(), [IDLE_HEADER]: String(idleSeconds()), ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...(headers ?? {}) },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (data ?? {}) as { code?: string; message?: string; details?: unknown; correlationId?: string };
    if (res.status === 401 && !path.startsWith('/auth/')) notifyUnauthorized(e.code);
    if (res.status === 409 && e.code === SESSION_CONFLICT) sessionConflict?.();
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? `Error ${res.status}`, e.details, e.correlationId);
  }
  return data as T;
}

export const apiGet = <T>(path: string, query?: Record<string, unknown>) => api<T>(path, { query });
export const apiPost = <T>(path: string, json?: unknown) => api<T>(path, { method: 'POST', json });
export const apiPut = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PUT', json });
export const apiPatch = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PATCH', json });
export const apiDelete = <T>(path: string, json?: unknown) => api<T>(path, { method: 'DELETE', json });

/** POST con JSON que responde un archivo (un PDF generado): el cuerpo como Blob; los errores, igual que `api`. */
export async function apiPostBlob(path: string, json: unknown): Promise<Blob> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...tenantHeader(), [IDLE_HEADER]: String(idleSeconds()), 'content-type': 'application/json' },
    body: JSON.stringify(json),
  });
  if (res.ok) return res.blob();
  const e = ((await res.json().catch(() => null)) ?? {}) as { code?: string; message?: string; details?: unknown; correlationId?: string };
  if (res.status === 401) notifyUnauthorized(e.code);
  if (res.status === 409 && e.code === SESSION_CONFLICT) sessionConflict?.();
  throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? `Error ${res.status}`, e.details, e.correlationId);
}

/** POST multipart (datos en `payload` y archivos) que responde un archivo: el cuerpo como Blob y sus cabeceras; los errores, igual que `api`. */
export async function apiFormBlob(path: string, payload: unknown, files: File[]): Promise<{ blob: Blob; headers: Headers }> {
  const fd = new FormData();
  fd.append('payload', JSON.stringify(payload));
  files.forEach((f) => fd.append('files', f, f.name));
  const res = await fetch(`${BASE}${path}`, { method: 'POST', credentials: 'include', headers: { ...tenantHeader(), [IDLE_HEADER]: String(idleSeconds()) }, body: fd });
  if (res.ok) return { blob: await res.blob(), headers: res.headers };
  const e = ((await res.json().catch(() => null)) ?? {}) as { code?: string; message?: string; details?: unknown; correlationId?: string };
  if (res.status === 401) notifyUnauthorized(e.code);
  if (res.status === 409 && e.code === SESSION_CONFLICT) sessionConflict?.();
  throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? `Error ${res.status}`, e.details, e.correlationId);
}

/** POST multipart con datos y archivos opcionales: los datos van como JSON en el campo `payload`. */
export async function apiForm<T>(path: string, payload: unknown, files: File[] = []): Promise<T> {
  const fd = new FormData();
  fd.append('payload', JSON.stringify(payload));
  files.forEach((f) => fd.append('files', f, f.name));
  return api<T>(path, { method: 'POST', body: fd });
}

export async function apiUpload<T>(path: string, files: File[], extraQuery?: Record<string, unknown>): Promise<T> {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f, f.name));
  return api<T>(path, { method: 'POST', body: fd, query: extraQuery });
}
